import { z } from "zod";
import { addDays, addMonths, addWeeks, format } from "date-fns";
import {
  Box,
  ChartLegend,
  Text,
  TimeSeriesChart,
  WidgetInteractive,
  WidgetEmpty,
  WidgetSkeleton,
  useCategoricalColors,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingField,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import {
  calculateSchema,
  compareFieldsSchema,
  measureFieldId,
  measureSchema,
  measureSettingFields,
  MEASURE_SETTING_FIELD,
  useFieldMeasures,
  useMeasureFormat,
  withLegacyDataKeys,
  type MeasureFormat,
} from "#app/widgets/dashboards/widget-measure";
import {
  MISSING_FIELD_DESCRIPTION,
  MISSING_FIELD_TITLE,
} from "#app/widgets/dashboards/use-dimension-slices";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { WIDGET_ACCENT } from "../shared/widget-accents";
import { seriesLineTitle } from "../shared/derived-titles";
import { SERIES_GRAINS, useSeriesLine, type LinePoint } from "./use-series-line";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

/**
 * `series-line` — a themed time-series line chart of projects over time on the
 * `TimeSeriesChart` finished chart: one line for the total, or one line per
 * stage ("stage over time"). Reads the `useSeriesLine` reader, which asks the
 * SHARED server count aggregation (`useIssueGroupCounts`) for exact per-period
 * counts (week/month buckets, optional running total) — no client sample. Each
 * split line wears its entity's OWN color (`useCategoricalColors` —
 * stage/type/workflow icon color, ramp fallback); a single total line uses the
 * first ramp hue. The Measure rows (`figure` / `calculate` / `compareFields` —
 * `measure` here is the DATE column, a key older cards store) swap the plotted
 * figure from the project count to a field aggregate per period (the sum of
 * Contract value signed each month); Compare with fields draw one line each,
 * and the Group by split steps aside for them. Serves the Custom "Line chart"
 * preset and the curated Stages and Outcomes lines.
 */
export const seriesLineConfigSchema = withLegacyDataKeys(
  z.looseObject({
    measure: z.enum(["created", "updated"]).default("created"),
    figure: measureSchema,
    calculate: calculateSchema,
    compareFields: compareFieldsSchema,
    groupBy: z
      .enum(["none", "stage", "type", "workflow", "assignee", "team", "source", "outcome"])
      .default("none"),
    grain: z.enum(SERIES_GRAINS).default("auto"),
    cumulative: z.boolean().default(false),
    area: z.boolean().default(false),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  }),
  { measure: "figure", compare: true },
);

export type SeriesLineConfig = z.infer<typeof seriesLineConfigSchema>;

// One row per bucket: the shared x fields plus each series' count under its
// series id. The x value is the numeric bucket start (ms), NOT the label:
// distinct timestamps keep buckets from colliding even when labels repeat,
// and they land in true chronological order.
interface SeriesLineRow {
  t: number;
  label: string;
  [seriesId: string]: number | string;
}

function changeFromPrevious(
  point: LinePoint,
  previous: LinePoint | undefined,
  measure: MeasureFormat,
) {
  if (!previous) return undefined;
  const delta = point.y - previous.y;
  if (delta === 0) return `No change from ${previous.label}`;
  return `${delta > 0 ? "+" : "−"}${measure.format(Math.abs(delta))} from ${previous.label}`;
}

function SeriesLineRender({ w, h, config }: WidgetRenderContext<SeriesLineConfig>) {
  const outcome = config.groupBy === "outcome";
  // An outcome line counts Updates, which carry no field: the Measure rows
  // read as a count and the split stands.
  const { asked, pending } = useFieldMeasures(
    outcome ? null : config.figure,
    config.calculate,
    config.compareFields,
  );
  const { series, bucket, loading, error } = useSeriesLine(
    config.measure,
    // Compared fields are the lines; a split on top would multiply them.
    asked.length > 1 ? "none" : config.groupBy,
    config.cumulative,
    config.filters,
    asked,
    pending,
    config.grain,
  );
  const measure = useMeasureFormat(asked);
  // Split lines are separated BY hue, so they take the ramp (and any stage or
  // team color the split carries). One unsplit line has nothing to tell apart
  // — it wears the family's accent.
  const sliceColors = useCategoricalColors(
    series,
    series.length > 1 ? undefined : { fallbackToken: WIDGET_ACCENT.seriesLine },
  );
  // Click-through: a press on the plot opens this card's Data drawer filtered
  // to the hovered bucket.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle(outcome ? "No Updates" : "No Projects", config.filters);

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="line" />;
  }
  if (series.length === 0) {
    return pending ? (
      <WidgetEmpty title={MISSING_FIELD_TITLE} description={MISSING_FIELD_DESCRIPTION} />
    ) : (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const compact = h < 3;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;
  const multi = series.length > 1;
  const useArea = config.area && !multi;
  const showLegend = multi && (h >= 4 || w >= 9);

  // Every series shares the reader's bucket spine, so the first series' points
  // name every bucket; the pivot hangs each series' count off its id.
  const points = series[0]?.points ?? [];
  const rows: SeriesLineRow[] = points.map((point, i) => {
    const row: SeriesLineRow = { t: point.t, label: point.label };
    for (const s of series) row[s.id] = s.points[i]?.y ?? 0;
    return row;
  });

  // Cap the x tick labels by span so many buckets don't crowd. The format
  // compacts by span — a single year drops the year, a multi-year span shows a
  // short "Jan '24" so buckets stay legible.
  const maxTicks = Math.max(3, Math.round(w * 1.2));
  const first = points[0];
  const last = points.at(-1);
  const multiYear =
    first !== undefined &&
    last !== undefined &&
    new Date(first.t).getFullYear() !== new Date(last.t).getFullYear();
  const axisFmt = multiYear ? "MMM ''yy" : bucket === "month" ? "MMM" : "MMM d";
  const formatTick = (v: number) => format(new Date(v), axisFmt);

  const rankedPoints = series
    .flatMap((s) => s.points.map((point) => ({ seriesLabel: s.label, point })))
    .sort((a, b) => b.point.y - a.point.y);
  const peak = rankedPoints[0];
  const runnerUp = rankedPoints[1];
  const dominantPeak =
    peak &&
    peak.point.y >= 100 &&
    peak.point.y >= (runnerUp?.point.y ?? 0) * 8
      ? peak
      : null;

  // An outcome bucket counts Update activities, not projects — no issue
  // predicate reproduces it, so the plot stays a figure rather than a door.
  const handleBucketClick =
    drill && instance && !outcome
      ? (row: SeriesLineRow) => {
          const bucketStart = new Date(row.t);
          const endMs =
            bucket === "month"
              ? addMonths(bucketStart, 1).getTime()
              : bucket === "week"
                ? addWeeks(bucketStart, 1).getTime()
                : addDays(bucketStart, 1).getTime();
          // The chip names the whole bucket period, never a single day: a
          // month bucket reads "January 2026", a week "Week of Jan 5, 2026"
          // — so a week's "MMM d" label can't be mistaken for one day.
          const periodLabel =
            bucket === "month"
              ? format(bucketStart, "MMMM yyyy")
              : bucket === "week"
                ? `Week of ${format(bucketStart, "MMM d, yyyy")}`
                : format(bucketStart, "MMM d, yyyy");
          // A plot press answers the whole bucket across every split line, so
          // it carries no dimension. A cumulative point is a running total, so
          // it reaches back to the start of time (−∞) rather than just this
          // bucket.
          drill.openData(instance.instanceId, {
            kind: "dateRange",
            dateField: config.measure === "updated" ? "lastUpdateDate" : "creationDate",
            startMs: config.cumulative ? Number.NEGATIVE_INFINITY : row.t,
            endMs,
            label: periodLabel,
          });
        }
      : undefined;

  return (
    <Box direction="column" fill="flex" gap="xs">
      {dominantPeak ? (
        <Text size="2xs" variant="subtle" align="right" tabular>
          {multi ? `${dominantPeak.seriesLabel} · ` : ""}
          {dominantPeak.point.label} peak · {measure.compact(dominantPeak.point.y)}
        </Text>
      ) : null}
      <WidgetInteractive
        fill="flex"
        cursor={handleBucketClick ? "pointer" : undefined}
      >
        <TimeSeriesChart<SeriesLineRow>
          data={rows}
          xKey="t"
          series={series.map((s, i) => ({
            key: s.id,
            colorToken: sliceColors.tokens[i],
          }))}
          area={useArea}
          curve="monotone"
          showGrid={showGrid}
          showAxes={showAxes}
          maxXTicks={maxTicks}
          xTickFormatter={formatTick}
          yTickFormatter={measure.compact}
          onPointClick={handleBucketClick}
          ariaLabel={`${measure.label} over time`}
          renderTooltip={(row) => {
            if (multi) {
              // The split tooltip is the legend rendered as a compact data
              // table: every line's count for the hovered bucket, in the
              // chart's own colors.
              return (
                <WidgetChartTooltip
                  label={row.label}
                  details={
                    <ChartLegend
                      direction="column"
                      items={series.map((s, i) => ({
                        label: s.label,
                        colorToken: sliceColors.tokens[i],
                        value: measure.format(Number(row[s.id] ?? 0)),
                      }))}
                    />
                  }
                />
              );
            }
            const only = series[0];
            const index = only ? only.points.findIndex((p) => p.t === row.t) : -1;
            const point = index >= 0 ? only?.points[index] : undefined;
            if (!point) return null;
            const previousPoint = index > 0 ? only?.points[index - 1] : undefined;
            return (
              <WidgetChartTooltip
                label={point.label}
                details={measure.detail(point.y, point.count)}
                footer={changeFromPrevious(point, previousPoint, measure)}
              />
            );
          }}
        />
      </WidgetInteractive>
      {showLegend ? (
        <ChartLegend
          items={series.map((s, i) => ({ label: s.label, colorToken: sliceColors.tokens[i] }))}
        />
      ) : null}
    </Box>
  );
}

/** The line's Measure switch lives under `figure` — `measure` is its date column. */
const FIGURE_SETTING_FIELD: WidgetSettingField = { ...MEASURE_SETTING_FIELD, key: "figure" };

const seriesLineSettings = (config: SeriesLineConfig): WidgetSettingsGroup[] => [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  // The Data section splits by axis role: the time axis (which date seeds the
  // buckets, how wide a bucket is) and the lines drawn over it (what each
  // plots, how the total splits).
  {
    title: "Time axis",
    fields: [
      // An outcome split plots Updates on their own date, so the Date row
      // steps aside for it; the Interval still applies.
      ...(config.groupBy === "outcome"
        ? []
        : [
            {
              key: "measure",
              kind: "select",
              label: "Date",
              icon: "trending-up",
              options: [
                { value: "created", label: "Projects created" },
                { value: "updated", label: "Projects updated" },
              ],
            } satisfies WidgetSettingField,
          ]),
      {
        key: "grain",
        kind: "select",
        label: "Interval",
        icon: "calendar",
        options: [
          { value: "auto", label: "Automatic" },
          { value: "week", label: "Weekly" },
          { value: "month", label: "Monthly" },
        ],
      } satisfies WidgetSettingField,
    ],
  },
  {
    title: "Lines",
    fields: [
      // An Update carries no field: the Measure rows step aside for an
      // outcome split.
      ...(config.groupBy === "outcome"
        ? []
        : measureSettingFields(FIGURE_SETTING_FIELD, measureFieldId(config.figure) !== null, true)),
      // Compared fields are the lines, so the split row steps aside for them.
      ...(config.groupBy !== "outcome" &&
      measureFieldId(config.figure) !== null &&
      (config.compareFields?.length ?? 0) > 0
        ? []
        : [
            {
              key: "groupBy",
              kind: "select",
              label: "Group by",
              icon: "layers",
              options: [
                { value: "none", label: "Total" },
                { value: "stage", label: "Stage" },
                { value: "type", label: "Type" },
                { value: "workflow", label: "Workflow" },
                { value: "assignee", label: "Assignee" },
                { value: "team", label: "Team" },
                { value: "source", label: "Source" },
                { value: "outcome", label: "Outcome" },
              ],
            } satisfies WidgetSettingField,
          ]),
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "cumulative", kind: "toggle", label: "Running total", icon: "bar-chart-increasing" },
      { key: "area", kind: "toggle", label: "Fill area", icon: "activity" },
      { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
      { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const seriesLineDef: WidgetModule<SeriesLineConfig> = {
  type: "series-line",
  title: "Line chart",
  dynamicTitle: (config, context) => seriesLineTitle(config, config.groupBy, context),
  icon: "trending-up",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6 },
  configSchema: seriesLineConfigSchema,
  settings: seriesLineSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "series-line.custom",
      label: "Line chart",
      description: "Track a count or a field total over time, as a line.",
      section: "custom" satisfies WidgetSection,
      // Lands named by its gallery label. The label is not the family's while
      // every choice is built in, so it sticks like a rename until the user
      // renames the card or brings a custom field in — then the title derives
      // from the field ("Deductible by Bid Type").
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "none" },
      defaultW: 6,
    },
    {
      key: "series-line.outcome",
      label: "Outcomes over time",
      description:
        "A line per outcome: Updates logged per period, in the outcome's tone.",
      section: "outcomes" satisfies WidgetSection,
      tags: ["activity", "outcomes"] satisfies readonly WidgetTag[],
      icon: PROPERTY_ICON.followUp,
      // Label matches the derived form — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { groupBy: "outcome" },
      locks: ["groupBy"],
      defaultW: 6,
    },
  ],
  render: SeriesLineRender,
};
